import React, { useEffect, useMemo, useState } from 'react'
import ky from '../Modules/ApiClient'
import toast from 'react-hot-toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleLeft,
  faAngleRight,
  faBroom,
  faCalendarXmark,
  faCheck,
  faLocationDot,
  faSpinner,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons'
import { API_URL } from '../Globals'

interface MoppiItem {
  _id: string
  firstname?: string
  lastname?: string
  address?: string
  zip?: string
  city?: string
  nextSweepDate: string
}

interface MoppiResponse {
  items: MoppiItem[]
  totalCount: number
  page: number
  perPage: number
  months: 6 | 12 | 24
  cutoffDate: string
}

const PER_PAGE = 20

const requestHeaders = () => ({
  'Content-Type': 'application/json',
  Token: localStorage.getItem('token') || ''
})

const dateOnly = (value: string): Date => new Date(`${value}T00:00:00`)

const formatDate = (value: string): string => (
  value ? dateOnly(value).toLocaleDateString('fi-FI') : 'Ei sovittua'
)

const daysOverdue = (value: string): number => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.max(0, Math.round((today.getTime() - dateOnly(value).getTime()) / 86_400_000))
}

const Moppi: React.FC = () => {
  const [months, setMonths] = useState<6 | 12 | 24>(6)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<MoppiResponse | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [clearing, setClearing] = useState(false)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    ky.get(`${API_URL}/api/address/maintenance/overdue-sweeps`, {
      searchParams: { months, page, perPage: PER_PAGE },
      headers: requestHeaders()
    }).json<MoppiResponse>()
      .then(response => {
        if (!active) return
        setData(response)
        setSelectedIds(current => new Set(Array.from(current).filter(id => response.items.some(item => item._id === id))))
      })
      .catch(() => {
        if (!active) return
        setData(null)
        setSelectedIds(new Set())
        setError('Mopin kohteita ei voitu ladata. Tarkista palvelinyhteys ja käyttöoikeus.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => { active = false }
  }, [months, page, refresh])

  const items = useMemo(() => data?.items || [], [data])
  const totalPages = Math.max(1, Math.ceil((data?.totalCount || 0) / PER_PAGE))
  const pageIds = useMemo(() => items.map(item => item._id), [items])
  const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id))

  const changeMonths = (value: 6 | 12 | 24) => {
    setMonths(value)
    setPage(1)
    setSelectedIds(new Set())
    setConfirming(false)
  }

  const toggleItem = (id: string) => {
    setSelectedIds(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setConfirming(false)
  }

  const togglePage = () => {
    setSelectedIds(current => {
      const next = new Set(current)
      if (allOnPageSelected) pageIds.forEach(id => next.delete(id))
      else pageIds.forEach(id => next.add(id))
      return next
    })
    setConfirming(false)
  }

  const clearSelected = async () => {
    if (selectedIds.size === 0) return
    setClearing(true)
    try {
      const result = await ky.post(`${API_URL}/api/address/maintenance/clear-overdue-sweeps`, {
        headers: requestHeaders(),
        json: { months, ids: Array.from(selectedIds) }
      }).json<{ clearedCount: number, skippedIds: string[] }>()
      toast.success(`${result.clearedCount} seuraavaa nuohousta tyhjennettiin`)
      if (result.skippedIds.length > 0) {
        toast(`${result.skippedIds.length} muuttunutta kohdetta ohitettiin turvallisesti`)
      }
      setSelectedIds(new Set())
      setConfirming(false)
      setRefresh(value => value + 1)
    } catch (requestError: any) {
      const body = await requestError?.response?.json?.().catch(() => null)
      toast.error(typeof body?.error === 'string' ? body.error : 'Seuraavien nuohousten tyhjentäminen epäonnistui.')
    } finally {
      setClearing(false)
    }
  }

  return (
    <main className="mobile-page container mx-auto px-3 pb-10 sm:px-6 lg:px-8">
      <section className="mobile-panel overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-100 bg-white px-4 py-4 sm:px-6 sm:py-5">
          <h1 className="text-2xl font-bold text-gray-900">Moppi</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-600">
            Moppi etsii aktiiviset kohteet, joiden sovittu nuohouspäivä on ollut myöhässä yli 6, 12 tai 24 kuukautta.
            Voit valita kohteet ja tyhjentää niiden vanhentuneen seuraavan nuohouspäivän.
            Kohteita tai muita asiakastietoja ei poisteta.
          </p>
        </header>

        <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="rounded-xl bg-gray-50 p-2" role="group" aria-label="Myöhästymisraja">
              <p className="px-2 pb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Myöhästymisraja</p>
              <div className="grid grid-cols-3 gap-2">
                {([6, 12, 24] as const).map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => changeMonths(value)}
                    className={`min-h-11 rounded-lg px-2 text-xs font-bold transition sm:px-4 sm:text-sm ${months === value ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    aria-pressed={months === value}
                  >
                    {value === 6 ? 'Yli 6 kk' : value === 12 ? 'Yli 1 vuosi' : 'Yli 2 vuotta'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Tarkistettavia kohteita</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{loading ? '–' : error ? '–' : data?.totalCount || 0}</p>
              {!error && data?.cutoffDate && <p className="mt-1 text-xs text-gray-500">Sovittu päivä ennen {formatDate(data.cutoffDate)}</p>}
            </div>
            <button
              type="button"
              onClick={togglePage}
              disabled={items.length === 0 || loading || Boolean(error)}
              className="min-h-11 rounded-xl border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCheck} className="mr-2 text-blue-600" />
              {allOnPageSelected ? 'Poista sivun valinnat' : 'Valitse sivun kaikki'}
            </button>
          </div>

          {loading && <div className="flex min-h-48 items-center justify-center text-blue-600"><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan kohteita…</div>}
          {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 font-medium text-red-700">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="flex min-h-48 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
              <FontAwesomeIcon icon={faCalendarXmark} className="text-3xl text-emerald-500" />
              <p className="mt-3 font-bold text-gray-800">Ei siivottavaa</p>
              <p className="mt-1 text-sm text-gray-500">Valitun rajan ylittäviä sovittuja nuohouksia ei löytynyt.</p>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200" aria-label="Mopin kohteet">
              {items.map(item => {
                const selected = selectedIds.has(item._id)
                const name = [item.firstname, item.lastname].filter(Boolean).join(' ') || 'Nimetön'
                return (
                  <label key={item._id} className={`flex cursor-pointer items-start gap-3 p-4 transition hover:bg-blue-50 ${selected ? 'bg-blue-50' : 'bg-white'}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleItem(item._id)} className="mt-1 h-5 w-5 flex-none accent-blue-600" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-gray-900">{name}</span>
                      <span className="mt-1 flex items-start gap-2 text-sm text-gray-600"><FontAwesomeIcon icon={faLocationDot} className="mt-0.5 flex-none text-gray-400" /><span>{item.address || 'Ei osoitetta'}{item.zip || item.city ? `, ${[item.zip, item.city].filter(Boolean).join(' ')}` : ''}</span></span>
                    </span>
                    <span className="flex-none text-right">
                      <span className="block text-xs font-medium text-gray-500">Seuraava nuohous</span>
                      <span className="mt-1 block font-bold text-red-600">{formatDate(item.nextSweepDate)}</span>
                      <span className="mt-0.5 block text-xs font-semibold text-red-500">Myöhässä {daysOverdue(item.nextSweepDate)} pv</span>
                    </span>
                  </label>
                )
              })}
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <nav className="flex items-center justify-center gap-2" aria-label="Mopin sivutus">
              <button type="button" onClick={() => { setPage(value => Math.max(1, value - 1)); setConfirming(false) }} disabled={page === 1} className="h-11 w-11 rounded-xl bg-gray-100 text-gray-700 disabled:opacity-40"><FontAwesomeIcon icon={faAngleLeft} /></button>
              <span className="flex h-11 min-w-24 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-bold text-white">{page} / {totalPages}</span>
              <button type="button" onClick={() => { setPage(value => Math.min(totalPages, value + 1)); setConfirming(false) }} disabled={page >= totalPages} className="h-11 w-11 rounded-xl bg-gray-100 text-gray-700 disabled:opacity-40"><FontAwesomeIcon icon={faAngleRight} /></button>
            </nav>
          )}

          <section className="rounded-xl border border-gray-200 bg-gray-50 p-4" aria-labelledby="moppi-action-title">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 id="moppi-action-title" className="font-bold text-gray-900">{selectedIds.size} kohdetta valittu</h2>
                <p className="mt-1 text-sm text-gray-500">Vain “Seuraava nuohous” tyhjennetään. Kaikki muu säilyy ennallaan.</p>
              </div>
              <button type="button" onClick={() => setConfirming(true)} disabled={selectedIds.size === 0 || clearing} className="min-h-12 rounded-xl bg-blue-600 px-5 font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><FontAwesomeIcon icon={faBroom} className="mr-2" />Tyhjennä valitut</button>
            </div>

            {confirming && (
              <div role="alertdialog" aria-modal="false" aria-labelledby="moppi-confirm-title" className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-1 flex-none text-amber-700" />
                  <div>
                    <h3 id="moppi-confirm-title" className="font-bold text-amber-950">Oletko aivan varma?</h3>
                    <p className="mt-1 text-sm leading-relaxed text-amber-900">Olet tyhjentämässä {selectedIds.size} kohteen seuraavan nuohouspäivän. Kohteet säilyvät, mutta päivämäärät täytyy tarvittaessa sopia ja lisätä uudelleen.</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button type="button" onClick={() => void clearSelected()} disabled={clearing} className="min-h-12 rounded-xl bg-amber-700 px-4 font-bold text-white transition hover:bg-amber-800 disabled:opacity-60">{clearing ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Tyhjennetään…</> : `Kyllä, tyhjennä ${selectedIds.size} päivää`}</button>
                  <button type="button" onClick={() => setConfirming(false)} disabled={clearing} className="min-h-12 rounded-xl border border-amber-300 bg-white px-4 font-bold text-amber-900 transition hover:bg-amber-100">Peruuta</button>
                </div>
              </div>
            )}
          </section>
        </div>
      </section>
    </main>
  )
}

export default Moppi
